import React from 'react';

import {type PartialDialogProps} from 'web-app/react/dialog/types';

export interface BlockNavigationPromptPayload {
    dialogProps: DialogProps;
    previousHash: string;
}

const NavigationBlockContext = React.createContext({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setNavigationBlockingPromptPayload: (payload: BlockNavigationPromptPayload | undefined) => {
        console.warn('Default NavigationBlockContext function was invoked - please provide a proper implementation.');
    },
});

export const {Provider: NavigationBlockProvider} = NavigationBlockContext;

/**
 * A hook that blocks navigation away from the current route whenever
 * the `when` condition is met.
 *
 * ⚠️ Do not use this hook in modals, unless the modal is opened/closed purely via route/query params.
 * This hook does not properly handle being unmounted without a navigation, and it will end up potentially
 * blocking navigation requests like reported in FBS-4947
 *
 * @param when - The condition that controls whether blocking is enabled
 * @param dialogProps - The props of the dialog shown when the user tries to navigate away
 */
const useBlockNavigation = (when: boolean, dialogProps: DialogProps) => {
    const {setNavigationBlockingPromptPayload} = React.useContext(NavigationBlockContext);
    /**
     * Destructuring the dialogProps here ensures that we are working with primitive values like strings and booleans.
     * This is beneficial for the useEffect below because it will only run if any of these values change.
     * If we passed the dialogProps as an object then that could lead up to an infinite loop if the object was not memoized.
     * Objects are reference types and their reference can change on every re-render, which in turn, would trigger the useEffect again, resulting in an infinite loop.
     */
    const {description, cancelText, okText, title, type, whiteList} = dialogProps;

    React.useEffect(() => {
        if (when) {
            setNavigationBlockingPromptPayload({
                dialogProps: {description, cancelText, okText, title, type, whiteList},
                previousHash: window.location.hash,
            });
        } else {
            setNavigationBlockingPromptPayload(undefined);
        }

        return () => setNavigationBlockingPromptPayload(undefined);
    }, [when, setNavigationBlockingPromptPayload, description, cancelText, okText, title, type, whiteList]);
};

export default useBlockNavigation;

/*
|------------------------------------------------------------------------------
| Helpers
|------------------------------------------------------------------------------
*/

/**
 * The props passed to the dialog shown when the user tries to navigate away.
 *
 * Narrowing the type of 'description' to string as useBlockNavigationForWebpackBuild
 * relies on momentarily serializing the dialog props.
 * See the 'message' prop passed to the 'Prompt' component in app/react/routes/application.tsx
 */
export type DialogProps = Pick<PartialDialogProps, 'title' | 'okText' | 'cancelText'> & {
    description: string;
    type?: 'mf';
    /**
     * An array of strings that should be ignored by the Prompt component in app/react/routes/application.tsx
     * Use this array to define unique parts of the URL that should be checked.
     */
    whiteList?: string[];
};
